import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Camera, NotebookPen, BellRing, Hexagon } from "lucide-react";
import TypewriterCycle from "@/components/TypewriterCycle";

// Three brand-aligned phrases that rotate in the H1's second line.
const HEADLINE_CYCLE = [
  "Track every drop.",
  "Catch every swarm.",
  "Spot every queen.",
];

const ICONS = { notebook: NotebookPen, bell: BellRing, camera: Camera, hexagon: Hexagon };

const HERO_PHOTO_FALLBACK = "https://images.unsplash.com/photo-1568526381923-caf3fd520382?w=900&q=80&auto=format&fit=crop";

// Decorative floating hex motes — fixed seed so they don't reshuffle on re-render.
const HEX_MOTES = [
  { size: 14, left: "8%",  delay: "-2s",  duration: "22s", x: "40px" },
  { size: 10, left: "22%", delay: "-12s", duration: "28s", x: "-30px" },
  { size: 18, left: "38%", delay: "-6s",  duration: "26s", x: "20px" },
  { size: 8,  left: "55%", delay: "-18s", duration: "30s", x: "-50px" },
  { size: 12, left: "72%", delay: "-4s",  duration: "24s", x: "35px" },
  { size: 16, left: "88%", delay: "-14s", duration: "32s", x: "-20px" },
];

/**
 * Landing hero — mouse-tracked parallax + Ken Burns photo + glassmorphism
 * stat cards. Receives the resolved CMS content as `c`.
 */
export default function Hero({ c }) {
  const photoStageRef = useRef(null);
  const inspectCardRef = useRef(null);
  const upcomingCardRef = useRef(null);
  const heroPhotoRef = useRef(null);
  const rafRef = useRef(0);

  // Mouse-tracked parallax for the floating glassmorphism cards.
  useEffect(() => {
    const stage = photoStageRef.current;
    if (!stage) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const apply = (e) => {
      const r = stage.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (heroPhotoRef.current) {
          heroPhotoRef.current.style.transform = `translate3d(${x * 6}px, ${y * 6}px, 0) rotateX(${-y * 1.5}deg) rotateY(${x * 1.5}deg)`;
        }
        if (inspectCardRef.current) {
          inspectCardRef.current.style.transform = `translate3d(${x * 14}px, ${y * 10}px, 0) rotate(${x * 1.5}deg)`;
        }
        if (upcomingCardRef.current) {
          upcomingCardRef.current.style.transform = `translate3d(${x * -18}px, ${y * -12}px, 0) rotate(${x * -2}deg)`;
        }
      });
    };
    const reset = () => {
      cancelAnimationFrame(rafRef.current);
      [heroPhotoRef, inspectCardRef, upcomingCardRef].forEach((r) => {
        if (r.current) r.current.style.transform = "";
      });
    };
    stage.addEventListener("mousemove", apply);
    stage.addEventListener("mouseleave", reset);
    return () => {
      cancelAnimationFrame(rafRef.current);
      stage.removeEventListener("mousemove", apply);
      stage.removeEventListener("mouseleave", reset);
    };
  }, []);

  // Split CMS title on the first sentence boundary so the typewriter cycle
  // appears on the second line.
  const idx = c.hero_title.indexOf(". ");
  const head = idx > 0 ? c.hero_title.slice(0, idx + 1) : c.hero_title;

  return (
    <section id="spy-hero" className="hero-stage max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-8 items-center px-6 py-20 lg:py-28" data-testid="landing-hero">
      {/* Floating decorative hex motes */}
      <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true">
        {HEX_MOTES.map((m, i) => (
          <span
            key={i}
            className="hex-mote"
            style={{
              left: m.left,
              animationDelay: m.delay,
              animationDuration: m.duration,
              "--mote-x": m.x,
            }}
          >
            <svg width={m.size * 2} height={m.size * 2} viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12,2 22,7 22,17 12,22 2,17 2,7" />
            </svg>
          </span>
        ))}
      </div>

      <div className="lg:col-span-7 space-y-7 fade-up">
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-[-0.03em] font-black text-stone-950 leading-[1.02]" data-testid="landing-hero-title">
          <span className="block">{head}</span>
          <span className="block text-amber-600">
            <TypewriterCycle
              phrases={HEADLINE_CYCLE}
              caretClassName="text-amber-500"
              testid="hero-title-typewriter"
            />
          </span>
        </h1>
        <p className="text-lg sm:text-xl text-stone-600 leading-relaxed font-medium max-w-2xl" data-testid="landing-hero-subtitle">
          {c.hero_subtitle}
        </p>
        <div className="flex flex-wrap gap-3 pt-2">
          <Link to={c.cta_primary_link || "/register"}>
            <Button size="lg" className="rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold px-7 h-12 shadow-[0_0_28px_rgba(245,158,11,0.5)] hover:-translate-y-0.5 hover:shadow-[0_0_36px_rgba(245,158,11,0.6)] transition-all duration-300" data-testid="cta-get-started">
              {c.cta_primary_label} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to={c.cta_secondary_link || "/login"}>
            <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-2 border-stone-200 hover:border-stone-300 bg-white hover:bg-stone-50 text-stone-900 font-semibold transition-all duration-300" data-testid="cta-sign-in">
              {c.cta_secondary_label}
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-3 pt-8 border-t border-stone-200/70 max-w-xl">
          {c.features.map((f, i) => {
            const I = ICONS[f.icon] || NotebookPen;
            return <Feature key={i} icon={I} label={f.label} />;
          })}
        </div>
      </div>

      <div className="lg:col-span-5 relative" ref={photoStageRef} style={{ perspective: "1200px" }}>
        <div ref={heroPhotoRef} className="ken-burns parallax-target aspect-[4/5] max-w-md mx-auto rounded-[2rem] overflow-hidden ring-1 ring-stone-200/60 shadow-[0_30px_80px_-30px_rgba(120,80,0,0.45)] relative" data-testid="hero-photo">
          <img
            src={c.hero_image_url || HERO_PHOTO_FALLBACK}
            alt="Beekeeper at a hive"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-white/30" />
        </div>
        <span aria-hidden="true" className="absolute inset-0 -z-10 rounded-[2rem] blur-3xl bg-gradient-to-br from-amber-300/40 via-yellow-300/30 to-transparent" />

        <div ref={inspectCardRef} className="parallax-target absolute -bottom-6 -left-6 sm:-left-10 bg-white/70 backdrop-blur-2xl border border-white/60 rounded-2xl p-4 w-56 shadow-[0_8px_32px_rgba(245,158,11,0.18)] hidden sm:block z-10" data-testid="landing-inspection-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-bold">{c.inspection_card.title}</div>
          <div className="font-display text-lg mt-1 font-bold text-stone-900">{c.inspection_card.subtitle}</div>
          <div className="text-xs text-stone-600">{c.inspection_card.body}</div>
        </div>
        <div ref={upcomingCardRef} className="parallax-target absolute -top-5 -right-3 sm:-right-8 bg-gradient-to-br from-amber-400/95 to-amber-500/95 backdrop-blur-md text-stone-950 border border-amber-300 rounded-2xl p-4 w-52 shadow-[0_12px_40px_rgba(245,158,11,0.4)] hidden sm:block z-10" data-testid="landing-upcoming-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-900 font-bold">{c.upcoming_card.title}</div>
          <div className="font-display text-lg mt-1 font-extrabold">{c.upcoming_card.subtitle}</div>
          <div className="text-xs text-amber-950">{c.upcoming_card.body}</div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center ring-1 ring-amber-200/60 shadow-[0_4px_14px_rgba(245,158,11,0.18)]">
        <Icon className="w-5 h-5 text-amber-700" />
      </div>
      <span className="text-sm text-stone-700 font-medium">{label}</span>
    </div>
  );
}
